import React, { useEffect, useMemo, useState } from 'react';
import {
  Appointment,
  Service,
  Venue,
  Customer,
  AddOn,
  Company,
  QuestionnaireSubmission,
  BookingAvailabilitySlot
} from '@/types';
import {
  Mail,
  Smartphone,
  Check,
  Edit2,
  X,
  Filter,
  XCircle,
  Calendar,
  FileText,
  AlertCircle,
  CheckCircle,
  Printer,
  Plus,
  Loader2,
  Trash2,
  MessageSquare,
  Lock,
  Database,
  Bell,
  Send,
  ArrowDown,
  ArrowUp
} from 'lucide-react';
import { ManualBookingModal } from './ManualBookingModal';
import { showAlertModal, showConfirmModal } from '@shared/components/common/AlertModal';
import { filterPastAvailabilitySlots, getAvailableTimeSlotsForDate } from '@/lib/scheduling';
import { api } from '@/services/api';
import { formatUkDate, normalizeDateOnly } from '@/lib/dates';
import { pickPreferredAvailableTime } from '@/lib/timeSlotSelection';
import { buildMapById } from '@/lib/collections';
import { Button } from '@shared/components/ui/Button';
import { Input } from '@shared/components/ui/Input';
import { Select } from '@shared/components/ui/Select';
import { StatusBadge } from '@shared/components/common/StatusBadge';
import { Pagination } from '@shared/components/common/Pagination';
import { EmptyRow, Table, TBody, TD, TH, THead, TR } from '@shared/components/ui/Table';
import { normalizeErrorMessage } from '@/lib/errorUtils';

interface BookingsTabProps {
  services: Service[];
  venues: Venue[];
  customers: Customer[];
  companies: Company[];
  addOns: AddOn[];
  submissions: QuestionnaireSubmission[];
  onSendReminder: (
    appointmentId: string,
    type: 'sms' | 'email' | 'form',
    templateType?: string
  ) => Promise<void> | void;
  onUpdateBooking: (booking: Appointment, notify: { email: boolean; sms: boolean }) => void;
  onAddBooking: (booking: Appointment) => void;
  onDeleteBooking: (id: string) => void;
  onViewCustomer: (customerId: string) => void;
  onForceRefresh?: () => void;
}

export const BookingsTab: React.FC<BookingsTabProps> = ({
  services,
  venues,
  customers,
  companies,
  addOns,
  submissions = [],
  onSendReminder,
  onUpdateBooking,
  onAddBooking,
  onDeleteBooking,
  onViewCustomer,
  onForceRefresh
}) => {
  type BookingStatus = Appointment['status'];
  const isBookingStatus = (value: string): value is BookingStatus => {
    return (
      value === 'booked' || value === 'completed' || value === 'cancelled' || value === 'noshow'
    );
  };

  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [isLoadingAppointments, setIsLoadingAppointments] = useState(false);
  const [appointmentsError, setAppointmentsError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [customerOptions, setCustomerOptions] = useState<Customer[]>(customers || []);
  const [companyOptions, setCompanyOptions] = useState<Company[]>(companies || []);

  const [statusFilter, setStatusFilter] = useState('all');
  const [venueFilter, setVenueFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [customerFilter, setCustomerFilter] = useState('');
  const [refFilter, setRefFilter] = useState('');
  const [bookingSortDir, setBookingSortDir] = useState<'asc' | 'desc'>('desc');
  const [isDeleting, setIsDeleting] = useState(false);

  const [editingBooking, setEditingBooking] = useState<Appointment | null>(null);
  const [editingBookingOriginal, setEditingBookingOriginal] = useState<Appointment | null>(null);
  const [isManualBookingOpen, setIsManualBookingOpen] = useState(false);
  const [notifyOptions, setNotifyOptions] = useState({ email: true, sms: true });
  const [isSendingReminder, setIsSendingReminder] = useState(false);
  const [editingAvailabilitySlots, setEditingAvailabilitySlots] = useState<
    BookingAvailabilitySlot[]
  >([]);
  const [hasEditingAvailabilityData, setHasEditingAvailabilityData] = useState(false);
  const [isLoadingEditingAvailability, setIsLoadingEditingAvailability] = useState(false);

  // Custom Reminder Confirmation State
  const [reminderModal, setReminderModal] = useState<{ id: string; type: 'sms' | 'email' } | null>(
    null
  );
  const servicesById = useMemo(() => buildMapById(services), [services]);
  const addOnsById = useMemo(() => buildMapById(addOns), [addOns]);
  const venuesById = useMemo(() => buildMapById(venues), [venues]);

  useEffect(() => {
    setPage(1);
  }, [
    statusFilter,
    venueFilter,
    dateFilter,
    serviceFilter,
    customerFilter,
    refFilter,
    bookingSortDir
  ]);

  useEffect(() => {
    if (customers?.length) {
      setCustomerOptions(customers);
    }
  }, [customers]);

  useEffect(() => {
    if (companies?.length) {
      setCompanyOptions(companies);
    }
  }, [companies]);

  useEffect(() => {
    let isActive = true;
    setIsLoadingAppointments(true);
    setAppointmentsError('');

    const q = refFilter || customerFilter || '';
    const params = {
      page,
      pageSize,
      status: statusFilter === 'all' ? undefined : statusFilter,
      venueId: venueFilter === 'all' ? undefined : venueFilter,
      serviceId: serviceFilter === 'all' ? undefined : serviceFilter,
      dateFrom: dateFilter || undefined,
      dateTo: dateFilter || undefined,
      q: q || undefined,
      sortBy: 'date',
      sortDir: bookingSortDir
    };

    api
      .listStaffBookings(params)
      .then((data) => {
        if (!isActive) return;
        setAppointments(data.items || []);
        setTotalCount(Number(data.totalCount || 0));
      })
      .catch((err: unknown) => {
        if (!isActive) return;
        setAppointments([]);
        setTotalCount(0);
        setAppointmentsError(normalizeErrorMessage(err, 'Failed to load bookings.'));
      })
      .finally(() => {
        if (isActive) setIsLoadingAppointments(false);
      });

    return () => {
      isActive = false;
    };
  }, [
    page,
    pageSize,
    statusFilter,
    venueFilter,
    dateFilter,
    serviceFilter,
    customerFilter,
    refFilter,
    bookingSortDir
  ]);

  const refreshBookings = async () => {
    setIsLoadingAppointments(true);
    setAppointmentsError('');
    try {
      const q = refFilter || customerFilter || '';
      const data = await api.listStaffBookings({
        page,
        pageSize,
        status: statusFilter === 'all' ? undefined : statusFilter,
        venueId: venueFilter === 'all' ? undefined : venueFilter,
        serviceId: serviceFilter === 'all' ? undefined : serviceFilter,
        dateFrom: dateFilter || undefined,
        dateTo: dateFilter || undefined,
        q: q || undefined,
        sortBy: 'date',
        sortDir: bookingSortDir
      });
      setAppointments(data.items || []);
      setTotalCount(Number(data.totalCount || 0));
    } catch (err: unknown) {
      setAppointments([]);
      setTotalCount(0);
      setAppointmentsError(normalizeErrorMessage(err, 'Failed to load bookings.'));
    } finally {
      setIsLoadingAppointments(false);
    }
  };

  const handleAddBooking = async (booking: Appointment) => {
    try {
      await Promise.resolve(onAddBooking(booking));
      await refreshBookings();
    } catch (err: unknown) {
      showAlertModal(normalizeErrorMessage(err, 'Failed to create booking.'));
    }
  };

  const clearFilters = () => {
    setStatusFilter('all');
    setVenueFilter('all');
    setDateFilter('');
    setServiceFilter('all');
    setCustomerFilter('');
    setRefFilter('');
  };

  const hasActiveFilters =
    statusFilter !== 'all' ||
    venueFilter !== 'all' ||
    serviceFilter !== 'all' ||
    Boolean(dateFilter) ||
    Boolean(customerFilter) ||
    Boolean(refFilter);

  const handleSaveEdit = async () => {
    if (editingBooking) {
      try {
        const isCancelling = editingBooking.status === 'cancelled';
        const selectedCompany = editingBooking.companyId
          ? companyOptions.find((company) => company.id === editingBooking.companyId)
          : null;

        if (
          !isCancelling &&
          selectedCompany?.requirePo &&
          !String(editingBooking.poNumber || '').trim()
        ) {
          showAlertModal('PO Number is required for this company.');
          return;
        }

        if (!isCancelling && !editingBooking.time) {
          showAlertModal('Please select an available time before updating this booking.');
          return;
        }
        await Promise.resolve(onUpdateBooking(editingBooking, notifyOptions));
        closeEditBooking();
        await refreshBookings();
      } catch (err: unknown) {
        showAlertModal(normalizeErrorMessage(err, 'Failed to update booking.'));
      }
    }
  };

  const handleDelete = async (id: string) => {
    const confirmed = await showConfirmModal(
      `Are you sure you want to delete appointment #${id}? This action is permanent.`,
      {
        title: 'Delete Appointment',
        confirmText: 'Delete',
        cancelText: 'Cancel',
        isDestructive: true
      }
    );
    if (!confirmed) return;

    setIsDeleting(true);
    try {
      await Promise.resolve(onDeleteBooking(id));
      closeEditBooking();
      await refreshBookings();
    } catch (err: unknown) {
      showAlertModal(`Failed to delete booking: ${normalizeErrorMessage(err, 'Unknown error')}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleConfirmSendReminder = async () => {
    if (reminderModal) {
      setIsSendingReminder(true);
      try {
        await Promise.resolve(onSendReminder(reminderModal.id, reminderModal.type));
        setReminderModal(null);
        await refreshBookings();
      } finally {
        setIsSendingReminder(false);
      }
    }
  };

  useEffect(() => {
    if (!editingBooking?.venueId || !editingBooking?.date || !editingBooking?.serviceId) {
      setEditingAvailabilitySlots([]);
      setHasEditingAvailabilityData(false);
      setIsLoadingEditingAvailability(false);
      return;
    }

    const dateValue = normalizeDateOnly(editingBooking.date);
    if (!dateValue) {
      setEditingAvailabilitySlots([]);
      setHasEditingAvailabilityData(false);
      setIsLoadingEditingAvailability(false);
      return;
    }

    let isCurrent = true;
    setIsLoadingEditingAvailability(true);
    setHasEditingAvailabilityData(false);

    api
      .getBookingAvailability({
        venueId: editingBooking.venueId,
        date: dateValue,
        serviceId: editingBooking.serviceId,
        addOnIds: editingBooking.addOnIds || [],
        excludeAppointmentId: editingBooking.id
      })
      .then((result) => {
        if (!isCurrent) return;
        const slots = filterPastAvailabilitySlots(dateValue, result?.slots || []);
        setEditingAvailabilitySlots(slots);
        setHasEditingAvailabilityData(true);
        setEditingBooking((prev) => {
          if (!prev) return prev;
          const nextTime = pickPreferredAvailableTime({
            currentTime: prev.time,
            slots
          });
          return nextTime === prev.time ? prev : { ...prev, time: nextTime };
        });
      })
      .catch(() => {
        if (!isCurrent) return;
        setEditingAvailabilitySlots([]);
        setHasEditingAvailabilityData(false);
      })
      .finally(() => {
        if (isCurrent) {
          setIsLoadingEditingAvailability(false);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [
    editingBooking?.id,
    editingBooking?.date,
    editingBooking?.venueId,
    editingBooking?.serviceId,
    editingBooking?.addOnIds
  ]);

  const getBookingDurationMinutes = (booking: Appointment) => {
    const service = servicesById.get(booking.serviceId);
    let duration = service?.durationMinutes || 30;
    (booking.addOnIds || []).forEach((id) => {
      const addon = addOnsById.get(id);
      if (addon) duration += addon.durationMinutes || 0;
    });
    return duration;
  };

  const getAddOnNames = (booking: Appointment) =>
    (booking.addOnIds || [])
      .map((id) => addOnsById.get(id)?.name)
      .filter(Boolean)
      .join(', ');

  const toggleBookingDateSort = () => {
    setBookingSortDir((prev) => (prev === 'desc' ? 'asc' : 'desc'));
  };

  const renderBookingDateSortIcon = () =>
    bookingSortDir === 'asc' ? (
      <ArrowUp className="w-3.5 h-3.5 text-slate-600" />
    ) : (
      <ArrowDown className="w-3.5 h-3.5 text-slate-600" />
    );

  const toggleEditingAddOn = (id: string) => {
    if (!editingBooking) return;
    const current = editingBooking.addOnIds || [];
    const next = current.includes(id)
      ? current.filter((addOnId) => addOnId !== id)
      : [...current, id];
    setEditingBooking({ ...editingBooking, addOnIds: next });
  };

  const openEditBooking = (booking: Appointment) => {
    const snapshot = { ...booking, addOnIds: [...(booking.addOnIds || [])] };
    setEditingBooking(snapshot);
    setEditingBookingOriginal(snapshot);
  };

  const closeEditBooking = () => {
    setEditingBooking(null);
    setEditingBookingOriginal(null);
  };

  const editingTimeOptions = (() => {
    if (!editingBooking) return [] as string[];

    const dateValue = normalizeDateOnly(editingBooking.date);
    if (!dateValue) return [];

    const [year, month, day] = dateValue.split('-').map(Number);
    const date = new Date(year, (month || 1) - 1, day || 1);
    if (Number.isNaN(date.getTime())) return [];

    const venue = venuesById.get(editingBooking.venueId);
    const generated = getAvailableTimeSlotsForDate(date, venue?.schedule, {
      requiredDurationMinutes: getBookingDurationMinutes(editingBooking)
    });
    const available = hasEditingAvailabilityData
      ? editingAvailabilitySlots.filter((slot) => slot.available).map((slot) => slot.time)
      : generated;

    if (!editingBooking.time || available.includes(editingBooking.time)) {
      return available;
    }

    return [editingBooking.time, ...available];
  })();

  const isCancellingEdit = editingBooking?.status === 'cancelled';
  const editingCompany = editingBooking?.companyId
    ? companyOptions.find((company) => company.id === editingBooking.companyId)
    : null;
  const isEditingCompanyPoRequired = Boolean(editingCompany?.requirePo);
  const editPoMissing =
    !isCancellingEdit &&
    isEditingCompanyPoRequired &&
    !String(editingBooking?.poNumber || '').trim();
  const editHasAvailableSlots = hasEditingAvailabilityData && editingAvailabilitySlots.length > 0;
  const editTimeRequired = Boolean(editingBooking);
  const editTimeMissing = editTimeRequired && !editingBooking?.time && !isCancellingEdit;
  const disableEditSave =
    isLoadingEditingAvailability ||
    editPoMissing ||
    (!isCancellingEdit &&
      (editTimeMissing || (hasEditingAvailabilityData && !editHasAvailableSlots)));

  return (
    <div className="animate-fade-in">
      {isManualBookingOpen && (
        <ManualBookingModal
          onClose={() => setIsManualBookingOpen(false)}
          onSave={handleAddBooking}
          services={services}
          venues={venues}
          addOns={addOns}
          servicesById={servicesById}
          venuesById={venuesById}
          addOnsById={addOnsById}
          customers={customerOptions}
          companies={companyOptions}
          appointments={appointments}
        />
      )}

      {/* Custom Reminder Popup */}
      {reminderModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-sm w-full text-center animate-scale-up border border-slate-100">
            <div
              className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${reminderModal.type === 'sms' ? 'bg-teal-50 text-teal-600' : 'bg-blue-50 text-blue-600'}`}
            >
              {reminderModal.type === 'sms' ? (
                <Smartphone className="w-10 h-10" />
              ) : (
                <Mail className="w-10 h-10" />
              )}
            </div>
            <h3 className="font-black text-xl text-slate-900 uppercase tracking-tight mb-3">
              Send Notification
            </h3>
            <p className="text-slate-500 text-sm mb-8 leading-relaxed font-medium">
              Do you want to send another reminder by{' '}
              <span className="font-black text-slate-800 uppercase">{reminderModal.type}</span>?
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setReminderModal(null)}
                disabled={isSendingReminder}
                className="py-3 px-6 text-slate-400 font-black uppercase text-[10px] tracking-widest hover:text-slate-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSendReminder}
                disabled={isSendingReminder}
                className={`py-3 px-6 text-white font-black uppercase text-[10px] tracking-widest rounded-2xl shadow-lg transition-all active:scale-95 ${reminderModal.type === 'sms' ? 'bg-teal-600 shadow-teal-600/20' : 'bg-blue-600 shadow-blue-600/20'}`}
              >
                {isSendingReminder ? 'Sending...' : 'Confirm & Send'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Booking Modal */}
      {editingBooking && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-scale-up border border-slate-200 max-h-[90vh] flex flex-col">
            <div className="p-6 border-b bg-slate-50 flex justify-between items-center">
              <div>
                <h3 className="font-bold text-lg text-slate-900 uppercase tracking-tight">
                  Edit Appointment
                </h3>
                <p className="text-[10px] text-slate-400 font-black uppercase">
                  Ref: {editingBooking.id}
                </p>
              </div>
              <button
                onClick={closeEditBooking}
                className="text-slate-400 hover:text-slate-600 p-1.5"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto">
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">
                  Status
                </label>
                <select
                  className="w-full border-2 border-slate-100 p-2.5 rounded-xl bg-slate-50 outline-none focus:border-teal-500 font-bold"
                  value={editingBooking.status}
                  onChange={(e) => {
                    const nextStatus = e.target.value;
                    if (!isBookingStatus(nextStatus)) return;
                    if (nextStatus === 'cancelled' && editingBookingOriginal) {
                      setEditingBooking({ ...editingBookingOriginal, status: nextStatus });
                      return;
                    }
                    setEditingBooking({ ...editingBooking, status: nextStatus });
                  }}
                >
                  <option value="booked">Booked</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="noshow">No Show</option>
                </select>
                {isCancellingEdit && (
                  <p className="text-[10px] text-amber-600 mt-1 font-bold">
                    Cancellation ignores appointment detail changes.
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">
                    Date
                  </label>
                  <input
                    type="date"
                    className="w-full border-2 border-slate-100 p-2.5 rounded-xl bg-slate-50 outline-none text-sm"
                    value={normalizeDateOnly(editingBooking.date)}
                    onChange={(e) => setEditingBooking({ ...editingBooking, date: e.target.value })}
                    disabled={isCancellingEdit}
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">
                    Time
                  </label>
                  <select
                    className="w-full border-2 border-slate-100 p-2.5 rounded-xl bg-slate-50 outline-none text-sm"
                    value={editingBooking.time}
                    onChange={(e) => setEditingBooking({ ...editingBooking, time: e.target.value })}
                    disabled={isLoadingEditingAvailability || isCancellingEdit}
                  >
                    {isLoadingEditingAvailability && (
                      <option value="">Loading availability...</option>
                    )}
                    {!isCancellingEdit &&
                      !isLoadingEditingAvailability &&
                      editingTimeOptions.length === 0 && (
                        <option value="">No available slots</option>
                      )}
                    {!isCancellingEdit &&
                      !isLoadingEditingAvailability &&
                      editingTimeOptions.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    {isCancellingEdit && (
                      <option value={editingBooking.time || ''}>
                        {editingBooking.time || 'No time selected'}
                      </option>
                    )}
                  </select>
                  {!isLoadingEditingAvailability &&
                    !isCancellingEdit &&
                    hasEditingAvailabilityData &&
                    editingAvailabilitySlots.length === 0 && (
                      <p className="text-[10px] text-red-600 mt-1 font-bold">
                        No available slots for this date/service. Choose another date or venue.
                      </p>
                    )}
                  {editTimeMissing && (
                    <p className="text-[10px] text-amber-600 mt-1 font-bold">
                      Select an available time to continue.
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">
                  Location
                </label>
                <select
                  className="w-full border-2 border-slate-100 p-2.5 rounded-xl bg-slate-50 outline-none text-sm"
                  value={editingBooking.venueId}
                  onChange={(e) =>
                    setEditingBooking({ ...editingBooking, venueId: e.target.value })
                  }
                  disabled={isCancellingEdit}
                >
                  {venues.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">
                  PO Number {isEditingCompanyPoRequired ? '(Required)' : '(Optional)'}
                </label>
                <input
                  type="text"
                  className={`w-full border-2 p-2.5 rounded-xl outline-none text-sm ${
                    editPoMissing ? 'border-red-200 bg-red-50' : 'border-slate-100 bg-slate-50'
                  }`}
                  value={editingBooking.poNumber || ''}
                  onChange={(e) =>
                    setEditingBooking({ ...editingBooking, poNumber: e.target.value })
                  }
                  disabled={isCancellingEdit}
                  placeholder={
                    isEditingCompanyPoRequired
                      ? 'PO Number required for this company'
                      : 'Enter PO number'
                  }
                />
                {editPoMissing && (
                  <p className="text-[10px] text-red-600 mt-1 font-bold">
                    PO Number is required for this company.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase mb-2">
                  Add-ons
                </label>
                {addOns.length === 0 ? (
                  <p className="text-xs text-slate-400">No add-ons available.</p>
                ) : (
                  <div className="space-y-2">
                    {addOns.map((addon) => {
                      const selected = (editingBooking.addOnIds || []).includes(addon.id);
                      return (
                        <label
                          key={addon.id}
                          className="flex items-center gap-2 text-xs font-bold text-slate-600"
                        >
                          <input
                            type="checkbox"
                            className="w-4 h-4 text-teal-600 rounded"
                            checked={selected}
                            disabled={isCancellingEdit}
                            onChange={() => toggleEditingAddOn(addon.id)}
                          />
                          <span>{addon.name}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase mb-1 flex items-center gap-2">
                  <MessageSquare className="w-3 h-3 text-slate-400" />
                  Technician-only note
                </label>
                <textarea
                  rows={3}
                  className="w-full border-2 border-slate-100 p-2.5 rounded-xl bg-slate-50 outline-none text-sm"
                  value={editingBooking.staffPrivateNote || ''}
                  onChange={(e) =>
                    setEditingBooking({ ...editingBooking, staffPrivateNote: e.target.value })
                  }
                  disabled={isCancellingEdit}
                  placeholder="Private note for the technician (not visible to the customer)."
                />
                <p className="text-[10px] text-slate-400 mt-1 flex items-center gap-1">
                  <Lock className="w-3 h-3" /> Visible to technicians only.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-100 p-4 rounded-2xl space-y-2">
                <p className="text-[10px] font-black text-blue-800 uppercase mb-2">
                  Notification Dispatch
                </p>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 text-blue-600 rounded"
                    checked={notifyOptions.email}
                    onChange={(e) =>
                      setNotifyOptions({ ...notifyOptions, email: e.target.checked })
                    }
                  />
                  <span className="text-xs font-bold text-blue-700">Send Email Update</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 text-blue-600 rounded"
                    checked={notifyOptions.sms}
                    onChange={(e) => setNotifyOptions({ ...notifyOptions, sms: e.target.checked })}
                  />
                  <span className="text-xs font-bold text-blue-700">Send SMS Confirmation</span>
                </label>
              </div>
            </div>

            <div className="p-6 border-t bg-slate-50 flex justify-between gap-3">
              <button
                onClick={() => handleDelete(editingBooking.id)}
                className="p-2 text-red-300 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all"
                title="Delete Record"
              >
                <Trash2 className="w-5 h-5" />
              </button>
              <div className="flex gap-2">
                <button
                  onClick={closeEditBooking}
                  className="px-6 py-2 text-slate-500 font-bold hover:bg-white rounded-xl transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={disableEditSave}
                  className="bg-teal-600 text-white px-8 py-2 rounded-xl font-black hover:bg-teal-700 shadow-lg shadow-teal-600/20 active:scale-95 transition-all text-xs uppercase tracking-widest"
                >
                  Update Booking
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-end mb-4 gap-4">
          <div>
            <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2">
              Bookings Overview
              <span className="text-sm font-normal text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                {appointments.length} Showing
              </span>
            </h3>
            <div className="flex items-center gap-4 mt-1">
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-1">
                <Database className="w-3 h-3" /> Rows in DB: {appointments.length}
              </p>
              {appointments.length === 0 && (
                <p className="text-[10px] font-black uppercase tracking-widest text-red-500 animate-pulse">
                  Database Empty - Create a booking to see data
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={clearFilters}
              variant="ghost"
              size="sm"
              className="px-2 text-slate-500 hover:text-red-600"
            >
              <XCircle className="w-4 h-4" /> Clear Filters
            </Button>
            <Button onClick={() => setIsManualBookingOpen(true)} variant="primary" size="sm">
              <Plus className="w-4 h-4" /> New Booking
            </Button>
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
          <Input
            type="text"
            placeholder="Search ID..."
            value={refFilter}
            onChange={(e) => setRefFilter(e.target.value)}
          />
          <Input
            type="text"
            placeholder="Search name..."
            value={customerFilter}
            onChange={(e) => setCustomerFilter(e.target.value)}
          />
          <Select value={serviceFilter} onChange={(e) => setServiceFilter(e.target.value)}>
            <option value="all">All Services</option>
            {services.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
          <Input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} />
          <Select value={venueFilter} onChange={(e) => setVenueFilter(e.target.value)}>
            <option value="all">All Locations</option>
            {venues.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </Select>
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All Statuses</option>
            <option value="booked">Booked</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="noshow">No Show</option>
          </Select>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <Table className="w-full text-left">
          <THead className="bg-slate-50 border-b">
            <TR>
              <TH>Ref</TH>
              <TH>Customer</TH>
              <TH>
                <button
                  type="button"
                  onClick={toggleBookingDateSort}
                  className="inline-flex items-center gap-1.5 hover:text-slate-800"
                  title="Toggle date sort"
                >
                  Service & Date
                  {renderBookingDateSortIcon()}
                </button>
              </TH>
              <TH>Add-ons</TH>
              <TH>Location</TH>
              <TH align="center">Forms</TH>
              <TH align="center">Status</TH>
              <TH align="center">Reminder</TH>
              <TH align="center">Actions</TH>
            </TR>
          </THead>
          <TBody className="divide-y divide-slate-100">
            {isLoadingAppointments ? (
              <EmptyRow colSpan={9} className="p-12 text-center text-slate-500">
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="w-10 h-10 text-slate-200 animate-spin" />
                  <p className="font-bold text-slate-800">Loading bookings...</p>
                </div>
              </EmptyRow>
            ) : appointmentsError ? (
              <EmptyRow colSpan={9} className="p-12 text-center text-slate-500">
                <div className="flex flex-col items-center gap-2">
                  <AlertCircle className="w-10 h-10 text-slate-200" />
                  <p className="font-bold text-slate-800">{appointmentsError}</p>
                </div>
              </EmptyRow>
            ) : appointments.length === 0 ? (
              <EmptyRow colSpan={9} className="p-12 text-center text-slate-500">
                {!hasActiveFilters ? (
                  <div className="flex flex-col items-center gap-2">
                    <Database className="w-10 h-10 text-slate-200" />
                    <p className="font-bold text-slate-800">Your database table is empty.</p>
                    <p className="text-xs">
                      No records found in Supabase. Use 'New Booking' to add one.
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <Filter className="w-10 h-10 text-slate-200" />
                    <p className="font-bold text-slate-800">No matches found.</p>
                    <p className="text-xs">Try clearing your filters to see more records.</p>
                  </div>
                )}
              </EmptyRow>
            ) : (
              appointments.map((app) => {
                const customer = customerOptions.find(
                  (c) => c.email.toLowerCase() === (app.customerEmail || '').toLowerCase()
                );
                const venue = venuesById.get(app.venueId);
                const company = companyOptions.find((c) => c.id === app.companyId);
                const addonNames = getAddOnNames(app);
                const isFormDone = app.questionnaireStatus === 'submitted';
                const isNotRequired = app.questionnaireStatus === 'not_required';

                return (
                  <TR key={app.id} className="hover:bg-slate-50">
                    <TD className="font-mono text-xs text-slate-500">{app.id}</TD>
                    <TD>
                      <button
                        onClick={() =>
                          app.customerId
                            ? onViewCustomer(app.customerId)
                            : customer && onViewCustomer(customer.id)
                        }
                        className="font-medium hover:text-teal-600 hover:underline text-left text-slate-900"
                        disabled={!customer && !app.customerId}
                      >
                        {app.customerName}
                      </button>
                      {company && (
                        <div className="text-xs text-slate-500 mt-1">Company: {company.name}</div>
                      )}
                      {!company && app.companyName && (
                        <div className="text-xs text-slate-500 mt-1">
                          Company: {app.companyName}
                        </div>
                      )}
                    </TD>
                    <TD className="text-sm">
                      <div className="font-medium">
                        {servicesById.get(app.serviceId)?.name || app.serviceName || 'N/A'}
                      </div>
                      <div className="text-slate-500 text-xs">
                        {formatUkDate(app.date, 'N/A')} at {app.time}
                      </div>
                    </TD>
                    <TD className="text-xs text-slate-500">{addonNames || 'None'}</TD>
                    <TD className="text-sm text-slate-600">
                      {venuesById.get(app.venueId)?.name || app.venueName || 'N/A'}
                    </TD>
                    <TD align="center">
                      {isNotRequired ? (
                        <span className="text-[10px] text-slate-300 uppercase font-bold">N/A</span>
                      ) : (
                        <span
                          className={`px-2 py-1 rounded text-[10px] uppercase font-bold ${isFormDone ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-amber-50 text-amber-600 border border-amber-100'}`}
                        >
                          {isFormDone ? 'Done' : 'Pending'}
                        </span>
                      )}
                    </TD>
                    <TD align="center">
                      <StatusBadge status={app.status} />
                    </TD>
                    <TD align="center">
                      {app.reminderStatus === 'sent' ? (
                        <span className="px-2 py-1 rounded text-[10px] uppercase font-bold bg-green-50 text-green-700 border border-green-100 flex items-center justify-center gap-1">
                          <Bell className="w-3 h-3" /> Sent
                        </span>
                      ) : app.reminderStatus === 'failed' ? (
                        <span
                          className="px-2 py-1 rounded text-[10px] uppercase font-bold bg-red-50 text-red-600 border border-red-100 flex items-center justify-center gap-1"
                          title={`Retry ${app.reminderRetryCount || 0}/5`}
                        >
                          <AlertCircle className="w-3 h-3" /> Failed{' '}
                          {app.reminderRetryCount ? `(${app.reminderRetryCount})` : ''}
                        </span>
                      ) : app.reminderStatus === 'skipped' ? (
                        <span className="text-[10px] text-slate-300 uppercase font-bold">
                          Skipped
                        </span>
                      ) : (
                        <span className="px-2 py-1 rounded text-[10px] uppercase font-bold bg-slate-50 text-slate-400 border border-slate-100">
                          Pending
                        </span>
                      )}
                    </TD>
                    <TD align="center">
                      <div className="flex justify-center items-center gap-2">
                        <button
                          onClick={() => openEditBooking(app)}
                          title="Edit Booking"
                          className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setReminderModal({ id: app.id, type: 'sms' })}
                          title="Send SMS Reminder"
                          className="p-1.5 text-slate-500 hover:text-teal-600 hover:bg-teal-50 rounded-md transition-colors"
                        >
                          <Smartphone className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setReminderModal({ id: app.id, type: 'email' })}
                          title="Send Email Reminder"
                          className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                        >
                          <Mail className="w-4 h-4" />
                        </button>
                      </div>
                    </TD>
                  </TR>
                );
              })
            )}
          </TBody>
        </Table>
      </div>
      <Pagination
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        isLoading={isLoadingAppointments}
      />
    </div>
  );
};
